import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import DialogContentText from '@mui/material/DialogContentText';
import { Trans, useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import { Dialog, TextV2 } from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { BulkUploadInviteeData } from './bulkUploadUtils';

type ConfirmUploadDialogProps = {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  inviteeList: BulkUploadInviteeData[];
};

const ExtractRowNumbersAsText = (entries: BulkUploadInviteeData[]): string =>
  entries.reduce<string>(
    (previous, current) =>
      previous === ''
        ? `${current.rowNumber + 1}`
        : `${previous}, ${current.rowNumber + 1}`,
    '',
  );

const ConfirmUploadDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  inviteeList,
}: ConfirmUploadDialogProps) => {
  const { t } = useTranslation();
  const inviteeValidList = inviteeList.filter(
    (invitee) => invitee.validEmail && invitee.validFullName,
  );
  const inviteeInvalidList = inviteeList.filter(
    (invitee) => !invitee.validEmail || !invitee.validFullName,
  );
  const inviteeInvalidEmails = inviteeInvalidList.filter(
    (invitee) => !invitee.validEmail,
  );
  const inviteeEmptyFields = inviteeInvalidList.filter(
    (invitee) => invitee.email === '' || invitee.fullName === '',
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirm-upload-dialog-title"
      aria-describedby="alert-dialog-description"
      title={t(
        'ui.event-management.events.add-learners.button.upload-learners',
      )}
      content={
        <>
          <DialogContentText id="alert-dialog-description">
            <Trans
              i18nKey="ui.event-management.events.add-learners.confirm-upload.body.bold"
              components={{
                bold: <TextV2 component="span" variant="bodyBold" />,
              }}
              values={{ count: inviteeList.length }}
            />
          </DialogContentText>
          <Box
            sx={{
              my: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <TextV2 variant="h4">
              <Trans
                i18nKey="ui.event-management.events.add-learners.invalid-entries-text.bold"
                components={{
                  bold: <TextV2 component="span" variant="bodyBold" />,
                }}
                values={{ count: inviteeInvalidList.length }}
              />
            </TextV2>
          </Box>
          <Box>
            <Grid container sx={{ flexDirection: 'column' }}>
              {inviteeInvalidEmails.length > 0 && (
                <Box
                  sx={{
                    my: (theme) => theme.spacing(spacingSizeMap.XS),
                  }}
                >
                  <TextV2 color="error" variant="bodyBold">
                    {t(
                      'ui.event-management.events.add-learners.invalid-email-text',
                      { count: inviteeInvalidEmails.length },
                    )}
                  </TextV2>
                  <TextV2 color="error">
                    {t(
                      'ui.event-management.events.add-learners.invalid-row-text',
                      {
                        count: inviteeInvalidEmails.length,
                        rows: ExtractRowNumbersAsText(inviteeInvalidEmails),
                      },
                    )}
                  </TextV2>
                </Box>
              )}
              {inviteeEmptyFields.length > 0 && (
                <Box
                  sx={{
                    my: (theme) => theme.spacing(spacingSizeMap.XS),
                  }}
                >
                  <TextV2 color="error" variant="bodyBold">
                    {t(
                      'ui.event-management.events.add-learners.empty-field-text',
                      { count: inviteeEmptyFields.length },
                    )}
                  </TextV2>
                  <TextV2 color="error">
                    {t(
                      'ui.event-management.events.add-learners.invalid-row-text',
                      {
                        count: inviteeEmptyFields.length,
                        rows: ExtractRowNumbersAsText(inviteeEmptyFields),
                      },
                    )}
                  </TextV2>
                </Box>
              )}
            </Grid>
          </Box>
          <Box
            sx={{
              my: (theme) => theme.spacing(spacingSizeMap.XS),
            }}
          >
            <Divider />
          </Box>
          <Box
            sx={{
              my: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <TextV2 variant="h4">
              <Trans
                i18nKey="ui.event-management.events.add-learners.valid-entries-text.bold"
                components={{
                  bold: <TextV2 component="span" variant="bodyBold" />,
                }}
                values={{ count: inviteeValidList.length }}
              />
            </TextV2>
          </Box>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Grid container sx={{ flexDirection: 'column' }}>
              {inviteeValidList.map((invitee) => (
                <Grid key={invitee.email} data-testid="bulk-upload-invitee">
                  <TextV2 variant="h4">{invitee.fullName}</TextV2>
                  <TextV2>{invitee.email}</TextV2>
                  <Box
                    sx={{
                      my: (theme) => theme.spacing(spacingSizeMap.XS),
                    }}
                  >
                    <Divider />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      }
      primaryButtonText={t(
        'ui.event-management.events.add-learners.confirm-upload.button',
        {
          count: inviteeValidList.length,
        },
      )}
      onSubmit={onConfirm}
      disablePrimaryButton={isLoading}
      secondaryButtonText={t(
        'ui.event-management.events.add-learners.cancel-upload.button',
      )}
    />
  );
};

export default ConfirmUploadDialog;
